import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import {
  createTriageRule,
} from "../lib/auth";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { useTriageRules } from "../hooks/useTriageRules";
import { SidebarTrigger } from "@components/ui/sidebar";

type TriageRulesPageProps = {
  navigate: (route: string) => void;
};

const statusOptions = ["new", "triage", "in_progress", "resolved"];
const priorityOptions = ["low", "medium", "high", "critical"];
const ownerOptions = ["team", "role", "agent", "human"];
const noneOptionValue = "__none__";

const triageRuleFormSchema = z
  .object({
    name: z.string().trim().min(1, "Rule name is required."),
    keywords: z.string(),
    priority: z.string(),
    status: z.string(),
    ownerType: z.string(),
    ownerId: z.string(),
  })
  .superRefine((values, ctx) => {
    const hasKeyword = values.keywords
      .split(",")
      .map((entry) => entry.trim())
      .some(Boolean);
    if (!hasKeyword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["keywords"], message: "Add at least one keyword." });
    }
  });
type TriageRuleFormValues = z.infer<typeof triageRuleFormSchema>;

const triageRuleFormDefaults: TriageRuleFormValues = {
  name: "",
  keywords: "",
  priority: noneOptionValue,
  status: noneOptionValue,
  ownerType: noneOptionValue,
  ownerId: "",
};

export function TriageRulesPage({ navigate }: TriageRulesPageProps) {
  const {
    orgId, canEditRules, rules: visibleRules, loadError, isLoading,
    updateError, updatingRuleId, reloadRules, toggleRule: updateRule,
  } = useTriageRules();
  const {
    callback: toggleRule,
  } = useAsyncCallback(updateRule);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { register, control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<TriageRuleFormValues>({
    resolver: zodResolver(triageRuleFormSchema),
    defaultValues: triageRuleFormDefaults,
  });

  useEffect(() => {
    setSubmitError(null);
    reset(triageRuleFormDefaults);
  }, [orgId, reset]);

  const {
    pending: handleCreatePending,
    callback: handleCreate,
  } = useAsyncCallback(handleSubmit(async (values) => {
    if (!canEditRules) return;
    setSubmitError(null);
    const keywordList = values.keywords.split(",").map((entry) => entry.trim()).filter(Boolean);
    const ownerType = values.ownerType === noneOptionValue ? "" : values.ownerType;
    const ownerId = values.ownerId.trim();
    try {
      await createTriageRule({
        name: values.name,
        keywords: keywordList,
        priority: values.priority === noneOptionValue ? undefined : values.priority,
        status: values.status === noneOptionValue ? undefined : values.status,
        triageOwner:
          ownerType && ownerId
            ? { type: ownerType as "team" | "role" | "agent" | "human", id: ownerId }
            : undefined,
      });
      reset(triageRuleFormDefaults);
      await reloadRules();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to create triage rule.";
      setSubmitError(message);
    }
  }));

  const emptyDescription = canEditRules
    ? "Add keywords and defaults to auto-route incoming work."
    : "An organization owner or administrator can add rules to route incoming work.";

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/inbox")}>
              Inbox
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Triage rules</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Triage Automation"
        title="Triage Rules"
        description="View how incoming work is routed and prioritized."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/inbox")}>
              Back to inbox
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-6">
        {loadError ? (
          <p className="text-sm text-red-400" role="alert">
            {loadError}
          </p>
        ) : null}

        {!canEditRules ? (
          <p className="text-sm text-muted-foreground">
            You can view rules. Organization owners and administrators can create or change them.
          </p>
        ) : null}

        {canEditRules ? <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Create rule</CardTitle>
          </CardHeader>
          <form onSubmit={handleCreate} noValidate>
            <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="rule-name">Rule name</Label>
              <Input
                id="rule-name"
                type="text"
                placeholder="Billing escalation"
                {...register("name")}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "rule-name-error" : undefined}
              />
              {errors.name ? (
                <p id="rule-name-error" role="alert" className="text-xs text-red-400">
                  {errors.name.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-keywords">Keywords</Label>
              <Input
                id="rule-keywords"
                type="text"
                placeholder="billing, invoice, charge"
                {...register("keywords")}
                aria-invalid={Boolean(errors.keywords)}
                aria-describedby={errors.keywords ? "rule-keywords-error" : "rule-keywords-help"}
              />
              <p id="rule-keywords-help" className="text-xs text-muted-foreground/70">Comma-separated.</p>
              {errors.keywords ? (
                <p id="rule-keywords-error" role="alert" className="text-xs text-red-400">
                  {errors.keywords.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-priority">Priority override</Label>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="rule-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={noneOptionValue}>No override</SelectItem>
                      {priorityOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-status">Status override</Label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="rule-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={noneOptionValue}>No override</SelectItem>
                      {statusOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-owner-type">Owner type</Label>
              <Controller
                name="ownerType"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="rule-owner-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={noneOptionValue}>None</SelectItem>
                      {ownerOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-owner-id">Owner id</Label>
              <Input
                id="rule-owner-id"
                type="text"
                placeholder="billing"
                {...register("ownerId")}
              />
            </div>
            {submitError ? (
              <p className="md:col-span-2 text-sm text-red-400" role="alert">
                {submitError}
              </p>
            ) : null}
            <div className="md:col-span-2">
              <Button loading={handleCreatePending} loadingLabel="Creating rule…" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create rule"}
              </Button>
            </div>
          </CardContent>
          </form>
        </Card> : null}

        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Existing rules</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {updateError ? <p role="alert" className="text-sm text-red-400">{updateError}</p> : null}
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Card
                    key={`triage-skeleton-${index}`}
                    className="border-border bg-card/60"
                  >
                    <CardHeader className="space-y-2">
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-3 w-28" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : visibleRules.length === 0 ? (
              <Empty className="border-border/60 bg-muted/40">
                <EmptyHeader>
                  <EmptyTitle>No rules configured yet</EmptyTitle>
                  <EmptyDescription className="text-muted-foreground">
                    {emptyDescription}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              visibleRules.map((rule) => (
                <div
                  key={rule.id}
                  className="rounded-lg border border-border bg-muted/60 p-3 text-sm text-foreground/90"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold">{rule.name}</p>
                      <p className="text-xs text-muted-foreground/70">
                        {rule.keywords.join(", ") || "No keywords"}
                      </p>
                    </div>
                    {canEditRules ? (
                      <Button
                        variant="outline"
                        loading={updatingRuleId === rule.id} loadingLabel="Updating rule…" disabled={updatingRuleId !== null}
                        onClick={() => void toggleRule(rule)}
                      >
                        {rule.enabled ? "Disable" : "Enable"}
                      </Button>
                    ) : <span>{rule.enabled ? "Enabled" : "Disabled"}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {rule.priority ? <span>Priority: {rule.priority}</span> : null}
                    {rule.status ? <span>Status: {rule.status}</span> : null}
                    {rule.triageOwner ? (
                      <span>
                        Owner: {rule.triageOwner.type} {rule.triageOwner.id}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </section>
    </PageShell>
  );
}
